import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, forkJoin, of, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { API } from 'src/app/core/constants/api';
import { CONSTANTS } from 'src/app/core/constants/constants';
import { PagedDatasheets } from 'src/app/core/interfaces/paged-datasheets';
import { SimulationMeta } from 'src/app/core/models/extra-models/simulation-meta.model';
import { Simulation } from 'src/app/core/models/simulation.model';
import { Clone } from 'src/app/shared/utils/clone';
import { RemoveProperty } from 'src/app/shared/utils/remove-property/remove-property';
import { IDBService } from '../../idb/idb.service';
import { PermissionsService } from '../permission/permissions.service';
import { SimulationManagerService } from '../simulation-manager.service';

@Injectable()
export class SimulationStorageService {
  constructor(
    private httpClient: HttpClient,
    private simulationService: SimulationManagerService,
    private permissionsService: PermissionsService,
    private ibsService: IDBService,
  ) {}

  getAll(page = 1, limit = 9): Observable<PagedDatasheets> {
    const endPoint = `${API.DOCUMENTS_VERSIONED}?pageNum=${page || 1}&numByPage=${limit}`;
    return this.httpClient
      .get<PagedDatasheets>(endPoint)
      .pipe(catchError((error) => throwError(error)));
  }

  filter(
    page = 1,
    limit = 9,
    filterParams = {},
    useDatasheet = false,
  ): Observable<PagedDatasheets> {
    const sourceServer = useDatasheet
      ? API.RISER_DATASHEET_FILTER_STORAGE
      : API.DOCUMENTS_FILTER_STORAGE;
    const endPoint = `${sourceServer}?pageNum=${page || 1}&numByPage=${limit}`;
    return this.httpClient
      .post<PagedDatasheets>(endPoint, filterParams)
      .pipe(catchError((error) => throwError(error)));
  }

  simpleSave(simulation: Simulation): Observable<Simulation> {
    if (simulation?.id && !this.permissionsService.canEdit()) {
      return of(null);
    }
    let observable = null;

    if (simulation.id) {
      observable = this.httpClient.put(API.DOCUMENTS_STORAGE, simulation);
    } else {
      observable = this.httpClient.post(API.DOCUMENTS_STORAGE, simulation);
    }

    return observable;
  }

  save(simulation: Simulation, triggerChanges = false): Observable<Simulation> {
    if (simulation?.id && !this.permissionsService.canEdit()) {
      return of(null);
    }

    let observable = null;
    const simulationCopy = Clone(simulation);
    RemoveProperty(simulationCopy, new Set([CONSTANTS.GRAPHIC_PROPERTIES]));

    if (simulation.id) {
      observable = this.httpClient.put(API.DOCUMENTS_STORAGE, simulationCopy);
    } else {
      observable = this.httpClient.post(API.DOCUMENTS_STORAGE, simulationCopy);
    }

    return observable.pipe(
      tap((simulationInfo: Simulation) => {
        const simulationObj: Simulation = simulation;
        if (simulation.id === this.simulationService.simulation.id) {
          this.update(simulationObj, triggerChanges);
        }
        return simulationInfo;
      }),
      catchError((error) => throwError(error)),
    );
  }

  updateMetadata(simulationId: number, metadata: SimulationMeta, triggerChanges = false) {
    const urlToQuery = `${API.DOCUMENTS_STORAGE}/${simulationId}/${API.DOCUMENTS_STORAGE_METADATA}`;
    const observable = this.httpClient.put(urlToQuery, metadata);

    return observable.pipe(catchError((error: HttpErrorResponse) => throwError(error))).pipe(
      tap((simulationInfo) => {
        const simulationObj: Simulation = simulationInfo as Simulation;
        this.update(simulationObj, triggerChanges);
        return simulationInfo;
      }),
    );
  }

  update(simulation: Simulation, triggerChanges = false) {
    this.simulationService.setSimulation(simulation, triggerChanges);
  }

  copy(id: number): Observable<Simulation> {
    return this.httpClient
      .post<Simulation>(`${API.DOCUMENTS_STORAGE}/${id}/${API.DOCUMENTS_COPY}`, {})
      .pipe(catchError((error) => throwError(error)));
  }

  createRevision(id: number): Observable<Simulation> {
    const revisionEndPoint = `${API.DOCUMENTS_STORAGE}/${id}/${API.CREATE_REVISION}`;
    return this.httpClient
      .post<Simulation>(revisionEndPoint, {})
      .pipe(catchError((error) => throwError(error)));
  }

  delete(id: number): Observable<any> {
    return this.httpClient.delete(`${API.DOCUMENTS_STORAGE}/${id}`).pipe(
      tap((_) => {
        this.ibsService.delete(CONSTANTS.STORAGE.SIMULATION);
      }),
      catchError((error) => throwError(error)),
    );
  }

  load(id: number, triggerChanges = false): Observable<any> {
    return this.httpClient
      .get(`${API.DOCUMENTS_STORAGE}/${id}`)
      .pipe(catchError((error) => throwError(error)))
      .pipe(
        tap((simulationInfo) => {
          const simulationObj: Simulation = simulationInfo as Simulation;
          this.update(simulationObj, triggerChanges);
          return simulationInfo;
        }),
      );
  }

  getById(id: number, useDatasheet = false): Observable<any> {
    const sourceServer = useDatasheet ? API.RISER_DATASHEET_STORAGE : API.DOCUMENTS_STORAGE;
    return this.httpClient
      .get(`${sourceServer}/${id}`)
      .pipe(catchError((error) => throwError(error)));
  }

  getSomeById(ids: number[], useDatasheet = false): Observable<any> {
    const requests = ids.map((id) => this.getById(id, useDatasheet), useDatasheet);
    return forkJoin(requests);
  }

  getFilters(): Observable<any> {
    return this.httpClient
      .get(API.DOCUMENTS_FILTERS_OPTIONS)
      .pipe(catchError((error) => throwError(error)));
  }

  getDuctInfo(): Observable<any> {
    return this.httpClient.get(API.DUCT_INFO).pipe(catchError((error) => throwError(error)));
  }
}
